import {
  DateRangeSelector,
  ENUM_DATE_RANGE,
  Table,
  TableData,
  TableRow,
  ToastMessage,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import { BackIcon, MenuVerticalIcon } from 'cokitchen-icons';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LOGISTICS_ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import './styles.scss';
import { formatNumber, getUserInitials, getUserName } from 'utils/functions';
import RiderDetailsSideModal from 'pages/logistics/partners/partner-modals/rider-details-modal';
import { RiderType } from 'models/rider.model';
import { apiService } from 'utils/api';
import { toast } from 'react-toastify';
import { CoKitchenType } from 'models/cokitchen.model';

type PolygonRidersType = {
  rider_id: string;
  first_name: string;
  last_name: string;
  name: string;
  count: string;
  total_amount: number;
  deduction: string;
  average_duration: string;
};
const tableHeaders = ['Rider Name', 'Trips Completed', 'Amount earned', 'Partner', 'Actions', ''];

const PolygonRiders = () => {
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [showRiderDetails, setShowRiderDetails] = useState(false);
  const [polygonRiders, setPolygonRiders] = useState<PolygonRidersType[]>([]);
  const [selectedRiderId, setSelectedRiderId] = useState('');
  const [totalRiders, setTotalRiders] = useState('');
  const [selectedRiderDetails, setSelectedRiderDetails] = useState<RiderType>();
  const [dateRange, setDateRange] = useState({
    date: getDateRangeSelector(ENUM_DATE_RANGE.ALL)
  });
  const { polygon_id } = useParams();
  const cokitchen_id = localStorage.getItem(STORAGE_KEYS.COKITCHEN);

  const cokitchenArray: CoKitchenType[] = JSON.parse(
    localStorage.getItem(STORAGE_KEYS.COKITCHEN_LIST) || '[]'
  );
  const activeCokitchen = cokitchenArray.find((cokitchen) => cokitchen.id === cokitchen_id);
  const activePolygon = activeCokitchen?.cokitchen_polygons.find(
    (polygon) => polygon.id === polygon_id
  );

  const getPolygonRiders = async () => {
    if (!polygon_id) return;
    setLoading(true);
    const {
      date: { endDate: end_time, startDate: start_time }
    } = dateRange;
    const params = {
      start_time,
      end_time,
      polygon_id,
      page: pageNumber
    };
    try {
      const res = await apiService.get(`logistics/riders/summary`, {
        params,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      if (res) {
        setPolygonRiders(res?.data?.data[0]?.riders);
        setPageCount(res?.data?.data[0]?.meta?.total_pages);
        setTotalRiders(res?.data?.data[0]?.meta?.total_riders);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPolygonRiders();
  }, [pageNumber, polygon_id, dateRange]);

  const getRiderDetails = async () => {
    const res = await apiService.get(`logistics/super-admin/rider-details/${selectedRiderId}`, {
      baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
    });

    if (res) {
      setSelectedRiderDetails(res?.data?.riders);
    }
  };

  useEffect(() => {
    getRiderDetails();
  }, [selectedRiderId]);

  const getSelectedPage = async (val: { selected: number }) => {
    const pageNo = val?.selected + 1;
    setPageNumber(pageNo);
  };
  return (
    <div>
      <Link
        to={LOGISTICS_ROUTE_KEYS.OVERVIEW}
        className='text-sm text-[#595959] flex gap-2 items-center w-[220px]'>
        <BackIcon />
        <p>Back to overview</p>
      </Link>

      <div className='flex justify-between items-center'>
        <h3 className='text-lg md:text-2xl font-bold text-[#1A1A1A] my-6'>
          {activePolygon?.name} Riders ({totalRiders})
        </h3>
        <DateRangeSelector
          onChange={setDateRange}
          defaultRange={ENUM_DATE_RANGE.THIS_WEEK}
          showTime
        />
      </div>
      {loading ? (
        <div className=' flex h-full items-center justify-center text-center min-h-[300px]'>
          Loading ...
        </div>
      ) : polygonRiders?.length === 0 ? (
        <div className=' flex h-full items-center justify-center text-center min-h-[300px]'>
          No Rider available
        </div>
      ) : (
        <div className='border border-[#DEE1E3] rounded-lg pt-3 overflow-x-auto whitespace-nowrap '>
          <Table
            loading={loading}
            headers={tableHeaders}
            canSelect
            pageCount={pageCount}
            onPageChange={getSelectedPage}>
            {polygonRiders?.map((item) => (
              <TableRow
                key={item.rider_id}
                canSelect
                onToggle={() => setSelectedRiderId(item.rider_id)}
                className='hover:bg-gray-12 table_row text-[#1A1A1A] '>
                <TableData className='flex gap-1 items-center'>
                  <div className='w-7 h-7 rounded-full border-[#B3E2FE] bg-[#E1F3FF] text-[#0EA3FD] flex items-center justify-center text-xs relative uppercase'>
                    {getUserInitials(item)}
                    <span className='absolute bg-[#73D260] w-2 h-2 rounded-full top-[1px] right-[1px]' />
                  </div>
                  <p>{getUserName(item)}</p>
                </TableData>
                <TableData>
                  <p>{item.count} trips completed</p>
                </TableData>
                <TableData>
                  <p>{formatNumber(item.total_amount)}</p>
                </TableData>
                <TableData>
                  <p>{item.name}</p>
                </TableData>
                <TableData>
                  <p
                    onClick={() => {
                      setSelectedRiderId(item.rider_id);
                      setShowRiderDetails(true);
                    }}
                    className='text-[#E60A2B] underline underline-offset-2'>
                    View details
                  </p>
                </TableData>
                <TableData>
                  <MenuVerticalIcon className='text-[#A3A3A3]' />
                </TableData>
              </TableRow>
            ))}
          </Table>
          <RiderDetailsSideModal
            show={showRiderDetails}
            onClose={() => setShowRiderDetails(false)}
            rider={selectedRiderDetails ?? ({} as RiderType)}
          />
        </div>
      )}
    </div>
  );
};

export default PolygonRiders;
